import React, { useEffect, useState, useMemo } from "react";
import Layouts from "../../Layouts";
import Npim from "../../../Assests/images/Npim.png";
import { MdKeyboardArrowDown } from "react-icons/md";
import { BsGrid3X3GapFill } from "react-icons/bs";
import { TfiLayoutGrid4Alt } from "react-icons/tfi";
import Card from "./Card";
import Sort from "./Sort";
import Pagination from "./Pagination";
import { Select, Empty, Spin } from "antd";
import ProductService from "../../../Redux/Services/ProductService";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";

// Helper function to extract a comparable product price
const getProductPrice = (product) => {
  if (!product) return 0;
  if (product.selectedProductPrice) return Number(product.selectedProductPrice);
  if (product.finalPrice) return Number(product.finalPrice);
  if (product.unitPrice) return Number(product.unitPrice);
  if (product.price) {
    if (Array.isArray(product.price)) return Number(product.price[0] || 0);
    return Number(product.price);
  }
  if (product.productPackageDetails && product.productPackageDetails.length > 0) {
    const pkg = product.productPackageDetails[0];
    if (pkg.finalPrice) return Number(pkg.finalPrice);
    if (pkg.unitPrice) return Number(pkg.unitPrice);
  }
  return 0;
};

const CATEGORIES = [
  { value: "ALL", label: "All Products" },
  { value: "Asavams & Aristas", label: "Asavams & Aristas" },
  { value: "Churnams", label: "Churnams" },
  { value: "Tailam", label: "Tailam" },
  { value: "Raw Herbs", label: "Raw Herbs" },
  { value: "Lehams", label: "Lehams" },
];

function Product() {
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [currentSort, setCurrentSort] = useState("featured");
  const [currentSortLabel, setCurrentSortLabel] = useState("Featured");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showThreeCards, setShowThreeCards] = useState(false);
  const [priceRange, setPriceRange] = useState(0);

  const fetchProductList = async (categoryCode) => {
    setLoading(true);
    try {
      if (categoryCode === "Asavams & Aristas") {
        // Fetch both Asavams and Aristas concurrently and combine results
        const [asavamsRes, aristasRes] = await Promise.allSettled([
          ProductService.ProductList({ status: 1, productCategoryCode: "Asavams" }),
          ProductService.ProductList({ status: 1, productCategoryCode: "Aristas" }),
        ]);

        const asavamsData =
          asavamsRes.status === "fulfilled" && Array.isArray(asavamsRes.value?.data)
            ? asavamsRes.value.data
            : [];
        const aristasData =
          aristasRes.status === "fulfilled" && Array.isArray(aristasRes.value?.data)
            ? aristasRes.value.data
            : [];

        // Combine and de-duplicate by id or code
        const combined = [...asavamsData, ...aristasData];
        const uniqueProducts = Array.from(
          new Map(combined.map((item) => [item.id || item.code, item])).values()
        );
        setAllProducts(uniqueProducts);
      } else {
        const payload = { status: 1 };
        if (categoryCode && categoryCode !== "ALL" && categoryCode !== "default") {
          payload.productCategoryCode = categoryCode;
        }
        const result = await ProductService.ProductList(payload);
        if (result?.data && Array.isArray(result.data)) {
          setAllProducts(result.data);
        } else {
          setAllProducts([]);
        }
      }
    } catch (error) {
      console.error("Error fetching product list:", error);
      setAllProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductList();
  }, []);

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    setCurrentPage(1);
    fetchProductList(value);
  };

  const handleSortChange = (sortOption) => {
    setCurrentSort(sortOption.id || sortOption.key);
    setCurrentSortLabel(sortOption.label);
    setCurrentPage(1);
  };

  const handlePriceRangeChange = (e) => {
    const val = Number(e.target.value);
    setPriceRange(val);
    setCurrentPage(1);
  };

  const handlePaginationChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 350, behavior: "smooth" });
  };

  const handleResetFilters = () => {
    setSelectedCategory("ALL");
    setCurrentSort("featured");
    setCurrentSortLabel("Featured");
    setPriceRange(0);
    setCurrentPage(1);
    fetchProductList("ALL");
  };

  const fetProductDetails = async (code) => {
    try {
      const response = await ProductService.productInvetoryData({ productCode: code });
      return response.data;
    } catch (err) {
      console.error("Error fetching inventory data:", err);
      return [];
    }
  };

  // Filter and Sort products client-side for immediate responsiveness
  const filteredAndSortedProducts = useMemo(() => {
    let list = [...allProducts];

    // 1. Client Category Filter
    if (selectedCategory && selectedCategory !== "ALL" && selectedCategory !== "default") {
      const targetCat = selectedCategory.toLowerCase();
      list = list.filter((item) => {
        const itemCat = String(item.productCategoryCode || item.category || "").toLowerCase();
        const itemName = String(item.name || "").toLowerCase();

        if (targetCat === "asavams & aristas") {
          return (
            itemCat.includes("asavam") ||
            itemCat.includes("arista") ||
            itemCat.includes("arishta") ||
            itemName.includes("asavam") ||
            itemName.includes("arista") ||
            itemName.includes("arishta") ||
            itemCat === "asavams" ||
            itemCat === "aristas"
          );
        }
        if (targetCat === "churnams") {
          return (
            itemCat.includes("churnam") ||
            itemCat.includes("choornam") ||
            itemName.includes("churnam") ||
            itemName.includes("choornam")
          );
        }
        if (targetCat === "tailam") {
          return (
            itemCat.includes("tailam") ||
            itemCat.includes("thailam") ||
            itemCat.includes("taila") ||
            itemName.includes("tailam") ||
            itemName.includes("thailam")
          );
        }
        if (targetCat === "raw herbs") {
          return (
            itemCat.includes("herb") ||
            itemCat.includes("raw") ||
            itemName.includes("herb") ||
            itemCat.includes("raw herbs")
          );
        }
        if (targetCat === "lehams") {
          return (
            itemCat.includes("leham") ||
            itemCat.includes("lehya") ||
            itemName.includes("leham") ||
            itemName.includes("lehya")
          );
        }
        return itemCat.includes(targetCat) || itemName.includes(targetCat);
      });
    }

    // 2. Price Range Filter
    if (priceRange > 0) {
      list = list.filter((item) => {
        const price = getProductPrice(item);
        return price <= priceRange;
      });
    }

    // 3. Sorting
    if (currentSort) {
      list.sort((a, b) => {
        const priceA = getProductPrice(a);
        const priceB = getProductPrice(b);
        const nameA = String(a.name || "").toLowerCase();
        const nameB = String(b.name || "").toLowerCase();

        switch (currentSort) {
          case "alphabeticalAZ":
            return nameA.localeCompare(nameB);
          case "alphabeticalZA":
            return nameB.localeCompare(nameA);
          case "priceLowToHigh":
            return priceA - priceB;
          case "priceHighToLow":
            return priceB - priceA;
          default:
            return 0;
        }
      });
    }

    return list;
  }, [allProducts, selectedCategory, priceRange, currentSort]);

  // Pagination calculations
  const itemsPerPage = showThreeCards ? 6 : 8;
  const totalProducts = filteredAndSortedProducts.length;
  const totalPages = Math.ceil(totalProducts / itemsPerPage) || 1;
  const safeCurrentPage = Math.min(currentPage, totalPages);

  const paginatedProducts = useMemo(() => {
    const startIdx = (safeCurrentPage - 1) * itemsPerPage;
    return filteredAndSortedProducts.slice(startIdx, startIdx + itemsPerPage);
  }, [filteredAndSortedProducts, safeCurrentPage, itemsPerPage]);

  const hasActiveFilters = selectedCategory !== "ALL" || currentSort !== "featured" || priceRange > 0;

  return (
    <Layouts>
      <div className="bg-[#FAF8F5] min-h-screen">
        {/* Hero Banner */}
        <div
          className="py-16 md:py-20 w-full bg-cover bg-center relative"
          style={{ backgroundImage: `url(${Npim})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
          <div className="container mx-auto px-6 md:px-12 relative z-10">
            <span className="text-amber-300 font-semibold tracking-wider text-xs md:text-sm uppercase mb-2 block">
              100% Authentic Classical Formulations
            </span>
            <h1 className="leading-tight font-serif font-bold text-3xl md:text-5xl text-[#FFF1DC] max-w-xl">
              Authentic <span className="text-amber-400">Ayurvedic Solutions</span> at a Click
            </h1>
            <p className="text-gray-200 text-sm md:text-base mt-3 max-w-lg font-light">
              Crafted in accordance with centuries-old classical texts for holistic health, wellness, and vitality.
            </p>
          </div>
        </div>

        {/* Toolbar & Product Showcase */}
        <div className="container mx-auto px-4 sm:px-6 md:px-12 py-8">
          {/* Controls Bar */}
          <div className="bg-white rounded-2xl border border-amber-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-4 md:p-5 mb-8">
            <div className="flex flex-col md:flex-row justify-between items-stretch md:items-center gap-4">
              {/* Left: Category & Sort Controls */}
              <div className="flex flex-wrap items-center gap-3 md:gap-5">
                {/* Category Filter */}
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase font-bold text-gray-500 tracking-wider flex items-center gap-1">
                    <FilterOutlined className="text-amber-700" /> Category:
                  </span>
                  <Select
                    value={selectedCategory}
                    onChange={handleCategoryChange}
                    className="min-w-[170px] custom-select rounded-lg"
                    size="middle"
                    dropdownStyle={{ borderRadius: "12px", padding: "6px" }}
                  >
                    {CATEGORIES.map((cat) => (
                      <Select.Option key={cat.value} value={cat.value}>
                        {cat.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* Sort By Dropdown */}
                <div className="relative">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase font-bold text-gray-500 tracking-wider">
                      Sort By:
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsSortOpen((prev) => !prev)}
                      className="px-3.5 py-1.5 bg-[#FAF3E0] hover:bg-[#F3E5C8] text-[#4C3000] font-semibold text-xs md:text-sm rounded-xl border border-amber-200/80 flex items-center gap-2 transition-all cursor-pointer shadow-sm"
                    >
                      <span>{currentSortLabel}</span>
                      <MdKeyboardArrowDown
                        className={`text-base transition-transform duration-200 ${
                          isSortOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                  </div>

                  {isSortOpen && (
                    <Sort
                      handleDropdownToggle={() => setIsSortOpen(false)}
                      handleSort={handleSortChange}
                      handlePriceChange={handlePriceRangeChange}
                      range={priceRange}
                      currentSort={currentSort}
                    />
                  )}
                </div>

                {/* Reset Filters Pill */}
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={handleResetFilters}
                    className="text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1 px-2.5 py-1 bg-red-50 hover:bg-red-100 rounded-lg transition-colors cursor-pointer"
                  >
                    <ReloadOutlined className="text-[10px]" /> Reset
                  </button>
                )}
              </div>

              {/* Right: Results Count & Grid Switcher */}
              <div className="flex items-center justify-between md:justify-end gap-4 pt-2 md:pt-0 border-t md:border-t-0 border-gray-100">
                <span className="text-xs text-gray-500 font-medium">
                  Showing <strong className="text-gray-800">{totalProducts > 0 ? (safeCurrentPage - 1) * itemsPerPage + 1 : 0}</strong>–
                  <strong className="text-gray-800">{Math.min(safeCurrentPage * itemsPerPage, totalProducts)}</strong> of{" "}
                  <strong className="text-amber-800">{totalProducts}</strong> products
                </span>

                {/* 3 vs 4 Grid Toggle (Desktop) */}
                <div className="hidden md:flex items-center gap-1.5 bg-amber-50/70 p-1 rounded-xl border border-amber-200/60">
                  <button
                    type="button"
                    onClick={() => setShowThreeCards(true)}
                    title="3 Cards per row"
                    className={`p-2 rounded-lg transition-all cursor-pointer ${
                      showThreeCards
                        ? "bg-[#A17A17] text-white shadow-sm"
                        : "text-gray-600 hover:text-[#A17A17] hover:bg-white"
                    }`}
                  >
                    <BsGrid3X3GapFill className="text-base" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowThreeCards(false)}
                    title="4 Cards per row"
                    className={`p-2 rounded-lg transition-all cursor-pointer ${
                      !showThreeCards
                        ? "bg-[#A17A17] text-white shadow-sm"
                        : "text-gray-600 hover:text-[#A17A17] hover:bg-white"
                    }`}
                  >
                    <TfiLayoutGrid4Alt className="text-base" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Product Grid Showcase */}
          {loading ? (
            <div className="flex flex-col justify-center items-center py-24">
              <Spin size="large" />
              <p className="text-gray-500 mt-4 font-medium text-sm">Loading authentic medicines...</p>
            </div>
          ) : paginatedProducts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-amber-100 p-12 text-center my-8 shadow-sm">
              <Empty
                description={
                  <div className="mt-2">
                    <h3 className="text-lg font-bold text-gray-800">No products found</h3>
                    <p className="text-gray-500 text-sm mt-1">
                      No products matched your selected category or price filters.
                    </p>
                  </div>
                }
              >
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="mt-4 px-5 py-2.5 bg-[#A17A17] hover:bg-[#85630e] text-white font-semibold text-sm rounded-xl transition-all shadow cursor-pointer"
                >
                  Clear All Filters
                </button>
              </Empty>
            </div>
          ) : (
            <div
              className={`grid gap-6 md:gap-8 justify-items-center ${
                showThreeCards
                  ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
                  : "grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
              }`}
            >
              {paginatedProducts.map((product, idx) => (
                <Card
                  key={product.id || idx}
                  product={product}
                  productInfo={async () => await fetProductDetails(product.code)}
                  showThreeCards={showThreeCards}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!loading && totalProducts > itemsPerPage && (
            <Pagination
              handlePagination={handlePaginationChange}
              Size={totalProducts}
              itemsPerPage={itemsPerPage}
              currentPage={safeCurrentPage}
            />
          )}
        </div>
      </div>
    </Layouts>
  );
}

export default Product;